import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { getUsers, updateUserRoles } from '@/lib/api-calls';
import { QueryKeys } from '@/lib/queries';
import { deactivate, isCoachRole, activateRole } from '@airreading/common';
import { UserRoleStatus, UserWithRolesAndProfiles } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';

export interface RevokeCoachProps {
  trigger: ReactNode;
  user: UserWithRolesAndProfiles;
}

export const ManageCoach = ({ trigger, user }: RevokeCoachProps) => {
  const [open, setOpen] = useState(false);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateUserRoles,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const isRevoking = user.userRoles.some((role) => isCoachRole(role) && role.status === UserRoleStatus.active);

  const onSubmit = () => {
    return mutateAsync({
      userId: user.id,
      roles: user.userRoles
        .map(({ role, schoolId, status }) => ({ role, schoolId, status }))
        .map((role) => (isCoachRole(role) ? (isRevoking ? deactivate(role) : activateRole(role)) : role)),
    })
      .then(() => {
        toast({
          title: isRevoking ? 'User teacher role revoked' : 'User teacher role restored',
        });
        setOpen(false);
        queryClient.invalidateQueries(getUsers());
        queryClient.invalidateQueries({ queryKey: QueryKeys.getUser(user.id) });
      })
      .catch(() => {
        toast({
          title: isRevoking ? 'Teacher revoke failed' : 'Teacher restore failed',
        });
      });
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{isRevoking ? 'Confirm to revoke?' : 'Confirm to restore?'}</AlertDialogTitle>
          <AlertDialogDescription>
            Do you want to {isRevoking ? 'revoke' : 'restore'}{' '}
            <span className="font-bold">{user.fullNameOrEmail}'s</span> teacher role?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              loading={isPending}
              onClick={(e) => {
                e.preventDefault();
                onSubmit();
              }}
            >
              {isRevoking ? 'Revoke' : 'Restore'}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
